import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { UserDetailsDialog } from '@/components/admin/UserDetailsDialog';
import { MediaThumbnailGrid } from '@/components/media/MediaThumbnailGrid';
import {
  fetchSupportTicketThread,
  replyToSupportTicket,
  selectSupportReplyError,
  selectSupportReplying,
  selectSupportSelectedMessages,
  selectSupportSelectedTicket,
  selectSupportThreadError,
  selectSupportThreadLoading,
  selectSupportUpdatingStatus,
  selectSupportUpdateStatusError,
  updateSupportTicketStatus,
} from '@/features/support';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  SupportTicketClosedReason,
  SupportTicketContextType,
  type ISupportMessage,
  type ISupportTicket,
} from '@/domain/entities/support';
import type { SupportStatusUpdateRequest } from '@/features/support/supportTypes';
import { formatUnixDateTimeCompact } from '@/lib';
import { getApiEndpoints } from '@/services/api/endpoints';
import { dynamicConfig } from '@/store/config';

type StatusOptionValue = 'open-support' | 'open-user' | 'closed-resolved' | 'closed-by-support';

const STATUS_OPTIONS: Array<{ value: StatusOptionValue; label: string; payload: SupportStatusUpdateRequest }> = [
  {
    value: 'open-support',
    label: 'Open • Waiting On Support',
    payload: { ticketID: '', status: 'open', awaiting: 'support' },
  },
  {
    value: 'open-user',
    label: 'Open • Waiting On User',
    payload: { ticketID: '', status: 'open', awaiting: 'user' },
  },
  {
    value: 'closed-resolved',
    label: 'Closed • Resolved',
    payload: { ticketID: '', status: 'closed', closedReason: 'resolved' },
  },
  {
    value: 'closed-by-support',
    label: 'Closed • Closed by Support',
    payload: { ticketID: '', status: 'closed', closedReason: 'closed_by_support' },
  },
];

const QUEUE_LABELS = {
  open: 'Open',
  closed: 'Closed',
} as const;

const AUTHOR_LABELS: Record<ISupportMessage['authorType'], string> = {
  homeowner: 'Homeowner',
  recovery_claimant: 'Claimant',
  support: 'Support',
  organization: 'Organization',
  system: 'System',
};

const AUTHOR_BADGE_VARIANTS: Record<
  ISupportMessage['authorType'],
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  homeowner: 'outline',
  recovery_claimant: 'outline',
  support: 'default',
  organization: 'secondary',
  system: 'secondary',
};

const AUTHOR_BADGE_CLASSES: Partial<Record<ISupportMessage['authorType'], string>> = {
  recovery_claimant:
    'border-sky-500/50 bg-sky-500/15 text-sky-700 dark:border-sky-400/50 dark:bg-sky-400/15 dark:text-sky-200',
};

type SubmittedRecoveryEvidence = {
  proofs: {
    existingEmailVerifiedAt?: number;
    newPhoneVerifiedAt?: number;
    contextSubmittedAt?: number;
  };
  contextAnswers: Array<{ promptKey: string; answer: string }>;
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseSubmittedRecoveryEvidence(value: unknown): SubmittedRecoveryEvidence | null {
  const source = asRecord(value);
  if (!source) return null;
  const proofs = asRecord(source.proofs);

  return {
    proofs: {
      existingEmailVerifiedAt: asNumber(proofs?.existingEmailVerifiedAt),
      newPhoneVerifiedAt: asNumber(proofs?.newPhoneVerifiedAt),
      contextSubmittedAt: asNumber(proofs?.contextSubmittedAt),
    },
    contextAnswers: Array.isArray(source.contextAnswers)
      ? source.contextAnswers.flatMap((entry) => {
          const answer = asRecord(entry);
          const promptKey = asString(answer?.promptKey);
          const text = asString(answer?.answer);
          return promptKey && text ? [{ promptKey, answer: text }] : [];
        })
      : [],
  };
}

function proofCompletion(timestamp?: number): string {
  return timestamp ? `Completed ${formatUnixDateTimeCompact(timestamp)}` : 'Not completed';
}

function humanizePromptKey(promptKey: string): string {
  return promptKey
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getRequesterLabel(ticket: ISupportTicket) {
  const fullName = [ticket.openedBySnapshot.firstName, ticket.openedBySnapshot.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || ticket.openedBySnapshot.email || ticket.openedByUserID;
}

function getWorkflowLabel(ticket: ISupportTicket): string {
  if (ticket.status === 'open') {
    return ticket.awaiting === 'user' ? 'Waiting On User' : 'Waiting On Support';
  }

  switch (ticket.closedReason) {
    case SupportTicketClosedReason.Resolved:
      return 'Resolved';
    case SupportTicketClosedReason.ClosedByHomeowner:
      return 'Closed by Homeowner';
    case SupportTicketClosedReason.ClosedBySupport:
      return 'Closed by Support';
    default:
      return 'Closed';
  }
}

function getWorkflowBadgeVariant(ticket: ISupportTicket): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (ticket.status === 'open') {
    return ticket.awaiting === 'user' ? 'secondary' : 'destructive';
  }

  return ticket.closedReason === SupportTicketClosedReason.Resolved ? 'default' : 'outline';
}

function getStatusOptionValue(ticket: ISupportTicket): StatusOptionValue {
  if (ticket.status === 'open') {
    return ticket.awaiting === 'user' ? 'open-user' : 'open-support';
  }

  return ticket.closedReason === 'resolved' ? 'closed-resolved' : 'closed-by-support';
}

function getStatusOptionPayload(ticketID: string, value: StatusOptionValue): SupportStatusUpdateRequest {
  const option = STATUS_OPTIONS.find((item) => item.value === value);

  if (!option) {
    return { ticketID, status: 'open', awaiting: 'support' };
  }

  return {
    ...option.payload,
    ticketID,
  };
}

function isSameWorkflowSelection(ticket: ISupportTicket, value: StatusOptionValue): boolean {
  const payload = getStatusOptionPayload(ticket.id, value);

  if (payload.status === 'open') {
    return ticket.status === 'open' && ticket.awaiting === payload.awaiting;
  }

  return ticket.status === 'closed' && ticket.closedReason === payload.closedReason;
}

export default function SupportTicketThreadPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const dispatch = useAppDispatch();

  const ticket = useAppSelector(selectSupportSelectedTicket);
  const messages = useAppSelector(selectSupportSelectedMessages);
  const isLoading = useAppSelector(selectSupportThreadLoading);
  const threadError = useAppSelector(selectSupportThreadError);
  const isReplying = useAppSelector(selectSupportReplying);
  const replyError = useAppSelector(selectSupportReplyError);
  const isUpdatingStatus = useAppSelector(selectSupportUpdatingStatus);
  const updateStatusError = useAppSelector(selectSupportUpdateStatusError);

  const [replyBody, setReplyBody] = useState('');
  const [nextStatus, setNextStatus] = useState<StatusOptionValue>('open-support');
  const [submittedRecoveryEvidence, setSubmittedRecoveryEvidence] = useState<SubmittedRecoveryEvidence | null>(null);
  const [isLoadingRecoveryEvidence, setIsLoadingRecoveryEvidence] = useState(false);
  const [recoveryEvidenceError, setRecoveryEvidenceError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketId) return;
    if (ticket?.id === ticketId && messages.length > 0) return;
    void dispatch(fetchSupportTicketThread({ ticketID: ticketId }));
  }, [dispatch, messages.length, ticket?.id, ticketId]);

  useEffect(() => {
    if (!ticket) return;
    setNextStatus(getStatusOptionValue(ticket));
  }, [ticket]);

  const recoveryCaseId =
    ticket?.contextType === SupportTicketContextType.IdentityRecovery ? ticket.contextID || undefined : undefined;

  useEffect(() => {
    let isCurrent = true;

    if (!recoveryCaseId) {
      setSubmittedRecoveryEvidence(null);
      setRecoveryEvidenceError(null);
      setIsLoadingRecoveryEvidence(false);
      return () => {
        isCurrent = false;
      };
    }

    setSubmittedRecoveryEvidence(null);
    setRecoveryEvidenceError(null);
    setIsLoadingRecoveryEvidence(true);

    void (async () => {
      try {
        const endpoint = `${getApiEndpoints().core.recoveryCases.getCase}/${encodeURIComponent(recoveryCaseId)}`;
        const response = await dynamicConfig.apiService.get<unknown>(endpoint, { suppressLogging: true });
        const parsedEvidence = parseSubmittedRecoveryEvidence(response);
        if (!parsedEvidence) throw new Error('Invalid recovery evidence response');
        if (isCurrent) setSubmittedRecoveryEvidence(parsedEvidence);
      } catch {
        if (isCurrent) {
          setRecoveryEvidenceError(
            'Submitted recovery evidence is temporarily unavailable. Open the recovery case to reload it.',
          );
        }
      } finally {
        if (isCurrent) setIsLoadingRecoveryEvidence(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [recoveryCaseId]);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0)),
    [messages],
  );

  const submitReply = async () => {
    const body = replyBody.trim();
    if (!ticket?.id || !body) return;

    try {
      await dispatch(
        replyToSupportTicket({
          ticketID: ticket.id,
          body,
        }),
      ).unwrap();
      setReplyBody('');
      toast.success('Reply sent');
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : null;
      toast.error(message || 'Failed to send reply');
    }
  };

  const submitStatusUpdate = async () => {
    if (!ticket?.id || isSameWorkflowSelection(ticket, nextStatus)) return;

    try {
      const payload = getStatusOptionPayload(ticket.id, nextStatus);
      await dispatch(updateSupportTicketStatus(payload)).unwrap();
      const label = STATUS_OPTIONS.find((option) => option.value === nextStatus)?.label ?? 'updated workflow';
      toast.success(`Ticket moved to ${label.toLowerCase()}`);
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : null;
      toast.error(message || 'Failed to update ticket status');
    }
  };

  if (isLoading && !ticket) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RouteBreadcrumb
        items={[{ label: 'Support Tickets', to: '/admin/support' }, { label: ticket?.title || ticketId || 'Ticket' }]}
      />

      <PageHeader
        title={ticket?.title || 'Support Ticket'}
        description={
          ticket ? (
            <span>
              Opened by{' '}
              <UserDetailsDialog
                userId={ticket.openedByUserID}
                trigger={
                  <button type="button" className="hover:text-foreground underline-offset-4 hover:underline">
                    {getRequesterLabel(ticket)}
                  </button>
                }
              />
            </span>
          ) : (
            'Support ticket thread'
          )
        }
        right={
          <div className="flex items-center gap-2">
            {ticket ? <Badge variant="outline">{QUEUE_LABELS[ticket.status]}</Badge> : null}
            {ticket ? <Badge variant={getWorkflowBadgeVariant(ticket)}>{getWorkflowLabel(ticket)}</Badge> : null}
            {ticket?.propertyID ? (
              <Button type="button" variant="outline" asChild>
                <Link to={`/properties/${encodeURIComponent(ticket.propertyID)}/edit`}>Open Property</Link>
              </Button>
            ) : null}
            {recoveryCaseId ? (
              <Button type="button" variant="outline" asChild>
                <Link to={`/admin/recovery-cases/${encodeURIComponent(recoveryCaseId)}`}>Review Recovery Case</Link>
              </Button>
            ) : null}
          </div>
        }
      />

      <ErrorNotice message={threadError} title="Failed to load support thread" />

      {ticket ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_380px]">
          <div className="space-y-4">
            {recoveryCaseId ? (
              <Card>
                <CardHeader>
                  <CardTitle>Submitted recovery questionnaire</CardTitle>
                  <CardDescription>
                    Submitted evidence is shown before the conversation. It is fetched only for this live administrator
                    session and is not stored with the support ticket.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isLoadingRecoveryEvidence ? (
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                      <LoadingSpinner size={20} /> Loading submitted recovery evidence…
                    </div>
                  ) : null}
                  <ErrorNotice message={recoveryEvidenceError} title="Recovery evidence unavailable" />
                  {submittedRecoveryEvidence ? (
                    <>
                      <div className="grid gap-3 text-sm sm:grid-cols-3">
                        <div>
                          <p className="text-muted-foreground">Existing email proof</p>
                          <p>{proofCompletion(submittedRecoveryEvidence.proofs.existingEmailVerifiedAt)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Replacement phone proof</p>
                          <p>{proofCompletion(submittedRecoveryEvidence.proofs.newPhoneVerifiedAt)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Questionnaire</p>
                          <p>{proofCompletion(submittedRecoveryEvidence.proofs.contextSubmittedAt)}</p>
                        </div>
                      </div>
                      {submittedRecoveryEvidence.contextAnswers.length ? (
                        <div className="space-y-3">
                          {submittedRecoveryEvidence.contextAnswers.map((entry) => (
                            <div key={entry.promptKey} className="rounded-lg border p-4">
                              <p className="text-muted-foreground text-xs font-medium">
                                {humanizePromptKey(entry.promptKey)}
                              </p>
                              <p className="mt-1 text-sm whitespace-pre-wrap">{entry.answer}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          No questionnaire answers were returned for this case.
                        </p>
                      )}
                    </>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle>Conversation</CardTitle>
                <CardDescription>{sortedMessages.length} messages in this thread</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {sortedMessages.length === 0 ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">No messages yet.</p>
                    {ticket.messageCount > 0 ? (
                      <p className="text-muted-foreground">
                        The support API returned no messages although this ticket reports {ticket.messageCount} message
                        {ticket.messageCount === 1 ? '' : 's'}. Refresh the page; if this continues, the support message
                        projection needs attention.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  sortedMessages.map((message) => (
                    <div key={message.id} className="rounded-lg border p-4">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <Badge
                          variant={AUTHOR_BADGE_VARIANTS[message.authorType]}
                          className={AUTHOR_BADGE_CLASSES[message.authorType]}
                        >
                          {AUTHOR_LABELS[message.authorType]}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {formatUnixDateTimeCompact(message.createdAt)}
                        </span>
                        <span className="text-muted-foreground text-xs">{message.authorID}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                      {ticket.propertyID && message.photos?.length ? (
                        <div className="mt-3 space-y-2">
                          <p className="text-muted-foreground text-xs font-medium">
                            {message.photos.length} {message.photos.length === 1 ? 'attachment' : 'attachments'}
                          </p>
                          <MediaThumbnailGrid
                            propertyID={ticket.propertyID}
                            folder="support"
                            imageKeys={message.photos.map((photo) => photo.key)}
                            altPrefix={`Support attachment for message ${message.id}`}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Reply</CardTitle>
                <CardDescription>
                  Sending a reply keeps the ticket open and moves it to waiting on user.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reply-body">Message</Label>
                  <Textarea
                    id="reply-body"
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    placeholder="Write your reply to the homeowner…"
                    rows={8}
                  />
                </div>
                <ErrorNotice message={replyError} title="Reply failed" />
                <div className="flex justify-end">
                  <Button type="button" onClick={submitReply} disabled={isReplying || !replyBody.trim()}>
                    {isReplying ? 'Sending…' : 'Send Reply'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Ticket Details</CardTitle>
                <CardDescription>Core metadata returned by the support API.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Requester</span>
                  <UserDetailsDialog
                    userId={ticket.openedByUserID}
                    trigger={
                      <button type="button" className="text-right underline-offset-4 hover:underline">
                        {getRequesterLabel(ticket)}
                      </button>
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Requester Email</span>
                  <span className="text-right">{ticket.openedBySnapshot.email || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Property ID</span>
                  <span className="text-right">{ticket.propertyID || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Queue</span>
                  <span className="text-right">{QUEUE_LABELS[ticket.status]}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Awaiting</span>
                  <span className="text-right">{ticket.awaiting}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Closed Reason</span>
                  <span className="text-right">{ticket.closedReason || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Category</span>
                  <span className="text-right">{ticket.category}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Priority</span>
                  <span className="text-right">{ticket.priority}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Source</span>
                  <span className="text-right">{ticket.source}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Last Message</span>
                  <span className="text-right">{formatUnixDateTimeCompact(ticket.lastMessageAt)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-right">{formatUnixDateTimeCompact(ticket.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Updated</span>
                  <span className="text-right">{formatUnixDateTimeCompact(ticket.updatedAt)}</span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-muted-foreground">Latest Preview</span>
                  <span className="max-w-[220px] text-right">{ticket.latestMessagePreview || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>

            {recoveryCaseId ? (
              <Card>
                <CardHeader>
                  <CardTitle>Recovery case actions</CardTitle>
                  <CardDescription>
                    Ticket replies support the conversation, but they cannot approve, reject, or execute recovery.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground text-sm">
                    Review the current case before acting. Approval and execution are intentionally separate actions in
                    the recovery case.
                  </p>
                  <Button type="button" className="w-full" asChild>
                    <Link to={`/admin/recovery-cases/${encodeURIComponent(recoveryCaseId)}`}>Review Recovery Case</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Workflow</CardTitle>
                  <CardDescription>Changing workflow creates a backend system message.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ticket-status">Next State</Label>
                    <Select value={nextStatus} onValueChange={(value) => setNextStatus(value as StatusOptionValue)}>
                      <SelectTrigger id="ticket-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <ErrorNotice message={updateStatusError} title="Status update failed" />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={submitStatusUpdate}
                      disabled={isUpdatingStatus || isSameWorkflowSelection(ticket, nextStatus)}
                    >
                      {isUpdatingStatus ? 'Updating…' : 'Update Workflow'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {ticket.appContext ? (
              <Card>
                <CardHeader>
                  <CardTitle>App Context</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Platform</span>
                    <span>{ticket.appContext.platform || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">App Version</span>
                    <span>{ticket.appContext.appVersion || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Build</span>
                    <span>{ticket.appContext.buildNumber || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">OS Version</span>
                    <span>{ticket.appContext.osVersion || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Device</span>
                    <span>{ticket.appContext.deviceModel || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
